import { Injectable, OnDestroy } from "@angular/core";
import { Subject, Subscription, Observable, interval } from "rxjs";
import { map, filter, distinctUntilChanged } from "rxjs/operators";
import getEnvironmentSettings from "../configs/env-conf-loader";
const ENVIRONMENT_SETTINGS = getEnvironmentSettings();

export interface ICookieChangeEvent extends Event {
    readonly changed: ICookie[];
    readonly deleted: ICookie[];
}

export interface ICookie {
    /**
     * A string containing the name of the cookie.
     */
    name: string;

    /**
     * A string containing the value of the cookie.
     */
    value: string;

    /**
     * A string containing the domain of the cookie.
     */
    domain: string;

    /**
     * A string containing the path of the cookie.
     */
    path: string;

    /**
     * A DOMTimeStamp containing the expiration date of the cookie.
     */
    expires: number;

    /**
     *  A boolean indicating whether the cookie is from a site with a secure context (HTTPS rather than HTTP).
     */
    secure: boolean;

    /**
     * One of the following SameSite values:
     *
     * `strict`: Cookies will only be sent in a first-party context and not be sent with requests initiated by third party websites.
     *
     * `lax`: Cookies are not sent on normal cross-site subrequests (for example to load images or frames into a third party site), but are sent when a user is navigating within the origin site (i.e. when following a link).
     *
     * `none`: Cookies will be sent in all contexts.
     */
    sameSite: "strict" | "lax" | "none";
}

@Injectable()
export class CookieService implements OnDestroy {

  private readonly _domain = ENVIRONMENT_SETTINGS.tenantSyncDomain;
  private readonly _currentTenantkey = ENVIRONMENT_SETTINGS.tenantSyncKey;
  private readonly _subjects = new Map<string, Subject<string>>();
  private readonly _subscriptions: Subscription[] = [];
  private _globalSubscription: Subscription | null = null;

  public readonly get = Object.assign(
    this._get,
    {
      currentTenant: () => this._get(this._currentTenantkey)
    }
  )

  private _get(key: string): string {
    const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${key}=`));
    return cookie ? cookie.split('=')[1] : null;
  }

  private _exist(key: string): boolean {
    const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${key}=`));
    return cookie ? true : false;
  }

  public readonly set = Object.assign(
    this._set,
    {
      currentTenant: (value: string) => this._set(this._currentTenantkey, value.toUpperCase())
    }
  );

  private _set(key: string, value: string): void {
    if (key && value) {
      document.cookie = `${key}=${value}; domain=${this._domain}; path=/; secure`;
    }
  }

  public readonly remove = Object.assign(
    this._remove,
    {
      currentTenant: () => this._remove(this._currentTenantkey)
    }
  );

  private _remove(key: string): void {
    if (key && this._exist(key)) {
      document.cookie = `${key}=; domain=${this._domain}; path=/; secure`;
    }
  }

  public readonly observe = Object.assign(
    this._observe,
    {
      currentTenant: () => this._observe(this._currentTenantkey)
    }
  );

  private _observe(key: string) {
    if (!this._globalSubscription) this._globalSubscription = this._createGlobalSubscription();
    let subject = this._subjects.get(key);
    if (!subject) {
      subject = new Subject<string>();
      this._subjects.set(key, subject);
    }
    return subject.asObservable().pipe(distinctUntilChanged());
  }

  private _createGlobalSubscription() {
    return this._createInternalTenantObservable()
      .subscribe(
        (changes) => changes.forEach(
          change => this._subjects.get(change.name)?.next(change.value)
        )
      );
  }

  private _createInternalTenantObservable(): Observable<{ name: string, value: string }[]> {
    try {
      /*@ts-ignore */
      if (cookieStore) {
        const subject = new Subject<{ name: string, value: string }[]>();
        /**@ts-ignore */
        cookieStore.addEventListener('change', (e: ICookieChangeEvent) => {
          const result: { name: string, value: string }[] = [];
          this._subjects.forEach((value, key) => {
            const change = e.changed.find((item) => item.name == key);
            if (change) result.push(change);
            else {
              const removed = e.deleted.find((item) => item.name == key);
              if (removed) result.push({ name: removed.name, value: "" });
            }
          })
          if (result.length > 0) subject.next(result);
        });
        return subject.asObservable();
      }
      else throw new Error("No cookieStore object");
    }
    catch {
      return interval(2500)
        .pipe(
          filter(() => this._subjects.size > 0 && this._subscriptions.length > 0),
          map(
            () => {
              const result: { name: string, value: string }[] = []
              this._subjects.forEach(
                (value, key) => result.push({
                  name: key,
                  value: this.get(key)
                }));
              return result;
            }
          )
        );
    }
  }

  /** @deprecated Use `observe` instead */
  public readonly subscribe = Object.assign(
    this._subscribe,
    {
      currentTenant: (subscriber: (value: string) => void) => this._subscribe(this._currentTenantkey, subscriber)
    }
  );

  /** @deprecated */
  private _subscribe(key: string, subscriber: (value: string) => void): Subscription {
    const subscription = this._observe(key).subscribe(subscriber);
    this._subscriptions.push(subscription);
    return subscription;
  }

  public ngOnDestroy(): void {
    this._unsubscribeAll();
  }

  private _unsubscribeAll() {
    this._subscriptions.forEach(sub => sub.unsubscribe());
    this._subjects.forEach(sub => sub.complete());
    this._subscriptions.splice(0, this._subscriptions.length);
    this._subjects.clear();
  }
}
